import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Plus, FolderPlus, FileText, Image, Search, Zap, Target, CheckSquare, Code, Magnet, Home as HomeIcon, Rocket, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { QuickChatSection } from '@/components/home/QuickChatSection';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DashboardButton from '@/components/ui/DashboardButton';
import { motion } from 'framer-motion';
import { GRID_BREAKPOINTS } from '@/config/grid-breakpoints';
import usePlanData from '@/hooks/token/use-plan-data';
import { URL_PARAMS } from '@/config/url-parameters';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import GradientButtonCentered from '@/components/ui/GradientButtonCentered';

// Animation variants
const cardVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

import { cn } from '@/lib/utils';

const Home = () => {
  const { subscriptionPlans, isLoading: plansLoading } = usePlanData();
  const navigate = useNavigate();
  const { openModal } = useLocalStorageState();
  
  const currentPlan = subscriptionPlans[0]; // Default to first plan if no specific plan selected



  // Quick actions data
  const quickActions = [
    {
      title: 'Chat with an AI Agent',
      description: 'Get instant AI assistance for any task or question',
      icon: MessageSquare,
      color: 'from-purple-500 to-blue-500',
      onClick: () => openModal(),
    },
    {
      title: 'Create Your First Document',
      description: 'AI transforms your brain fog into brilliant content that actually gets read',
      icon: FileText,
      color: 'from-red-500 to-red-600',
      onClick: () => navigate('/private-text-generator'),
      testId: 'create-page-button',
    },
    {
      title: 'Use AI Generate an Image',
      description: 'Make Picasso jealous with zero artistic talent required',
      icon: Image,
      color: 'from-blue-500 to-blue-600',
      onClick: () => navigate('/private-image-generator'),
    },
    {
      title: 'Use AI to Generate Research',
      description: 'From zero to PhD-level knowledge in minutes',
      icon: Search,
      color: 'from-green-500 to-green-600',
      onClick: () => navigate('/private-research-generator'),
    },
    {
      title: 'Create a Project',
      description: 'Let AI do the heavy lifting while you take all the credit',
      icon: FolderPlus,
      color: 'from-purple-500 to-purple-600',
      onClick: () => navigate(`/project-manager?${URL_PARAMS.MODALS.CREATE_PROJECT}=true`),
      testId: 'create-project-button',
    },
    {
      title: 'Create a Task',
      description: 'Turn chaos into organized productivity magic',
      icon: CheckSquare,
      color: 'from-amber-500 to-amber-600',
      onClick: () => navigate(`/task-manager?${URL_PARAMS.MODALS.CREATE_TASK}=true`),
    },
  ];

  return (
    <Container>
      <DashboardHeader 
        title="Welcome"
        description="Great to see you! Here's what's happening with your AI tools today"
        icon={<HomeIcon size={24} />}
        hideOnMobile={false}
      />

      
      <div className="space-y-6">
        {/* Top Row: Quick Actions (50%) + AI Usage (50%) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions - Left Half */}
          <Card className="bg-gradient-to-br from-white/80 via-white/90 to-white/80 dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 shadow-xl h-full">
            <CardContent className="p-6 h-full flex flex-col">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Quick Actions</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Ready to serve your every digital whim</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
                {quickActions.map((action, index) => (
                  <motion.button
                    key={action.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ 
                      scale: 1.05,
                      y: -5,
                      transition: { 
                        duration: 0.2, 
                        ease: "easeOut"
                      }
                    }}
                    whileTap={{ 
                      scale: 0.98,
                      transition: { 
                        duration: 0.1, 
                        ease: "easeInOut"
                      }
                    }}
                    transition={{ delay: index * 0.1 }}
                    onClick={action.onClick}
                    data-test-id={action.testId}
                    className={cn(
                      "group relative p-6 rounded-lg text-left transition-shadow duration-300 h-full",
                      "bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm",
                      "border border-gray-200/50 dark:border-gray-700/50",
                      "flex flex-col justify-center space-y-3",
                      "shadow-md hover:shadow-xl"
                    )}
                    style={{
                      willChange: 'transform'
                    }}
                  >
                    {/* Icon at top with individual hover animation */}
                    <motion.div 
                      className={`w-8 h-8 rounded-lg bg-gradient-to-br ${action.color} flex items-center justify-center shadow-lg`}
                      whileHover={{ 
                        rotate: [0, -5, 5, 0],
                        scale: 1.1,
                        transition: { 
                          duration: 0.6, 
                          ease: "easeInOut"
                        }
                      }}
                    >
                      <action.icon className="w-4 h-4 text-white" />
                    </motion.div>
                    
                    {/* Title - bigger */}
                    <h4 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
                      {action.title}
                    </h4>
                    
                    {/* Description */}
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      {action.description}
                    </p>
                  </motion.button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Chat - Right Half */}
          <div className="h-full">
            <QuickChatSection />
          </div>
        </div>


        {/* Custom Development Section */}
        <div className="mt-16 hidden" id="select-your-plan">
          <DashboardHeader
            title="Custom Apps"
            icon={<Rocket size={24} />}
            hideOnMobile={false}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Operations Center App */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/80 via-white/90 to-white/80 dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-blue-500/10 dark:shadow-blue-500/5 hover:shadow-2xl hover:shadow-blue-500/20 dark:hover:shadow-blue-500/10 transition-all duration-300 group">
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-indigo-500/5 dark:from-blue-500/10 dark:to-indigo-500/10"></div>
              
              <div className="relative p-8">
                {/* Header with icon and pricing */}
                <div className="mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          Auto-Ops
                        </h3>
                      </div>
                    </div>
                    {/* Desktop price */}
                    <div className="hidden sm:block text-right">
                      <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                        $10k
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Starting Price</p>
                    </div>
                  </div>
                  {/* Mobile price */}
                  <div className="sm:hidden mt-4 ml-16 flex items-baseline gap-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      $10k
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Starting Price</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  Complete Operations Automation
                </p>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {[
                    'Project Management & Tracking',
                    'Automated Invoicing & Payments',
                    'Talent Acquisition System Automations',
                    'Staff Onboarding System',
                    'Advanced Business Intelligence',
                    'Custom Mobile & Web Applications',
                    'Enterprise System Integrations',
                    'Multi-Platform Automation Suite',
                    'Scalable Infrastructure & Operations',
                    'Dedicated Success Manager',
                    'Priority Support & Ongoing Training'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-400 to-indigo-400 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <GradientButtonCentered
                  variant="blue"
                  onClick={() => window.open('mailto:team@finalpiece.ai', '_blank')}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  }
                >
                  Contact Us
                </GradientButtonCentered>
              </div>
            </div>

            {/* Sales Magnet App */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/80 via-white/90 to-white/80 dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-green-500/10 dark:shadow-green-500/5 hover:shadow-2xl hover:shadow-green-500/20 dark:hover:shadow-green-500/10 transition-all duration-300 group">
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-emerald-500/5 dark:from-green-500/10 dark:to-emerald-500/10"></div>
              
              <div className="relative p-8">
                {/* Header with icon and pricing */}
                <div className="mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                        <Magnet className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                          Sales Magnet
                        </h3>
                      </div>
                    </div>
                    {/* Desktop price */}
                    <div className="hidden sm:block text-right">
                      <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        $15k
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Starting Price</p>
                    </div>
                  </div>
                  {/* Mobile price */}
                  <div className="sm:hidden mt-4 ml-16 flex items-baseline gap-2">
                    <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                      $15k
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Starting Price</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  Sales & Marketing Engine
                </p>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {[
                    'High-Converting Landing Page',
                    'Email Marketing Automation',
                    'Performance Analytics Dashboard',
                    'Smart Lead Capture & CRM',
                    'Social Media Integration',
                    'Custom Mobile & Web Applications',
                    'Enterprise System Integrations',
                    'Multi-Platform Automation Suite',
                    'White-Label Solution & Branding',
                    'Dedicated Success Manager',
                    'Priority Support & Ongoing Training'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <GradientButtonCentered
                  variant="green"
                  onClick={() => window.open('mailto:team@finalpiece.ai', '_blank')}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  }
                >
                  Contact Us
                </GradientButtonCentered>
              </div>
            </div>
          </div>
        </div>
      </div>

    </Container>
  );
};

export default Home;
